import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { UserCard } from '@shared/components/ui/usercard';
import { usePermissions } from '@shared/hooks/usePermissions';
import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderWithoutWeight } from '../models/Order';

// 🎯 Helper: formatea la fecha en formato DD/MM/AAAA H:M
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const useAgGridColumns = () => {
  const { t } = useTranslation('ordersWithoutWeightModule');
  const { getTeamType } = usePermissions();
  const currency = useCurrency();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  const allColumnDefs = useMemo<ColDef<OrderWithoutWeight>[]>(() => {
    const baseColumns: ColDef<OrderWithoutWeight>[] = [
      {
        colId: 'id',
        field: 'id',
        headerName: t('table.id'),
        sortable: true,
        minWidth: 100,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.id ? <span className="text-sm font-medium text-title">{data.id}</span> : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'ref',
        field: 'ref',
        headerName: t('table.ref'),
        sortable: true,
        minWidth: 150,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.ref ? <span className="text-sm font-medium text-title">{data.ref}</span> : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'is_migrated',
        field: 'is_migrated',
        headerName: t('table.is_migrated'),
        sortable: false,
        minWidth: 140,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) => {
          if (data?.is_migrated === undefined || data?.is_migrated === null) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          return (
            <span className="text-sm text-title">
              {data.is_migrated ? t('table.migrated_yes') : t('table.migrated_no')}
            </span>
          );
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'courier',
        field: 'courier.name',
        headerName: t('table.courier'),
        sortable: false,
        minWidth: 150,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.courier ? (
            <span className="text-sm text-title">{data.courier.name}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'tracking_number',
        field: 'tracking_number',
        headerName: t('table.tracking_number'),
        sortable: false,
        minWidth: 150,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.tracking_number ? (
            <span className="text-sm text-title">{data.tracking_number}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'shop',
        field: 'shop.name',
        headerName: t('table.shop'),
        sortable: false,
        minWidth: 150,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.shop ? (
            <span className="text-sm text-title">{data.shop.name}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'team',
        field: 'shop.team.name',
        headerName: t('table.team'),
        sortable: false,
        minWidth: 150,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          hasTeamData && data?.shop?.team ? (
            <UserCard name={data.shop.team.name} email="" />
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'processed_at',
        field: 'processed_at',
        headerName: t('table.processed_at'),
        sortable: true,
        minWidth: 180,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.processed_at ? (
            <span className="text-sm text-title">{formatDateTime(data.processed_at)}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'last_shipment_update',
        field: 'last_shipment_update',
        headerName: t('table.last_shipment_update'),
        sortable: true,
        minWidth: 180,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.last_shipment_update ? (
            <span className="text-sm text-title">{formatDateTime(data.last_shipment_update)}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'shipment_status',
        field: 'shipment_status.name',
        headerName: t('table.shipment_status'),
        sortable: false,
        minWidth: 150,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.shipment_status ? (
            <span className="text-sm text-title">{data.shipment_status.name}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'total',
        field: 'total',
        headerName: t('table.total'),
        sortable: true,
        minWidth: 120,
        type: 'numericColumn',
        cellStyle: { textAlign: 'left' },
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.total !== undefined ? (
            <span className="text-sm font-medium text-title">{formatCurrencyAmount(data.total, currency)}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'created_at',
        field: 'created_at',
        headerName: t('table.created_at'),
        sortable: true,
        minWidth: 180,
        cellRenderer: ({ data }: { data: OrderWithoutWeight }) =>
          data?.created_at ? (
            <span className="text-sm text-title">{formatDateTime(data.created_at)}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
    ];

    // 🎯 Filtrar columna de team si no es tenant_3pl
    return hasTeamData ? baseColumns : baseColumns.filter((col) => col.colId !== 'team');
  }, [t, hasTeamData, currency]);

  return { allColumnDefs };
};
